import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Card,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, ArrowBack, BusinessCenter, Delete, Edit } from '@mui/icons-material';
import apiClient from '../lib/axios';

interface InsuranceCompany {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

const NAVY = '#0D1B35';
const BLUE = '#0066CC';

export const InsuranceList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState<InsuranceCompany | null>(null);
  const [insuranceName, setInsuranceName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: insuranceCompanies, isLoading, error: queryError } = useQuery({
    queryKey: ['insurance-companies'],
    queryFn: async () => {
      const { data } = await apiClient.get<InsuranceCompany[]>('/insurance-companies');
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      await apiClient.post('/insurance-companies', { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurance-companies'] });
      setCreateDialogOpen(false);
      setInsuranceName('');
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to create insurance company');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      await apiClient.put(`/insurance-companies/${id}`, { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurance-companies'] });
      setEditDialogOpen(false);
      setSelectedInsurance(null);
      setInsuranceName('');
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update insurance company');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/insurance-companies/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurance-companies'] });
      setDeleteDialogOpen(false);
      setSelectedInsurance(null);
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to delete insurance company');
    },
  });

  const handleCreateClick = () => {
    setInsuranceName('');
    setError(null);
    setCreateDialogOpen(true);
  };

  const handleEditClick = (insurance: InsuranceCompany) => {
    setSelectedInsurance(insurance);
    setInsuranceName(insurance.name);
    setError(null);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (insurance: InsuranceCompany) => {
    setSelectedInsurance(insurance);
    setError(null);
    setDeleteDialogOpen(true);
  };

  const handleCreateConfirm = () => {
    if (insuranceName.trim().length >= 2) {
      createMutation.mutate(insuranceName.trim());
    }
  };

  const handleEditConfirm = () => {
    if (selectedInsurance && insuranceName.trim().length >= 2) {
      updateMutation.mutate({ id: selectedInsurance.id, name: insuranceName.trim() });
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedInsurance) {
      deleteMutation.mutate(selectedInsurance.id);
    }
  };

  const allInsurance = insuranceCompanies ?? [];

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          borderRadius: 4,
          bgcolor: NAVY,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          p: { xs: '28px 24px', sm: '40px 44px' },
          mb: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', bgcolor: 'rgba(245,158,11,0.1)', top: -80, right: -60, pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', width: 140, height: 140, borderRadius: '50%', bgcolor: 'rgba(245,158,11,0.06)', bottom: -50, left: '35%', pointerEvents: 'none' }} />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <IconButton size="small" onClick={() => navigate('/admin/dashboard')} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.5 }}>
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase' }}>
                SEGUROS
              </Typography>
            </Box>
            <Typography variant="h2" sx={{ color: '#fff', mb: 0.5 }}>
              Compañías de Seguro
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: { xs: 13, sm: 14 } }}>
              {allInsurance.length} compañías registradas
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateClick}
            sx={{ bgcolor: BLUE, flexShrink: 0, mt: { xs: 0, sm: 1 }, '&:hover': { bgcolor: '#0052a3' } }}
          >
            {isMobile ? 'Agregar' : 'Agregar Compañía'}
          </Button>
        </Box>
      </Box>

      {queryError && (
        <Alert severity="error" sx={{ mb: 2 }}>Failed to load insurance companies. Please try again.</Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {allInsurance.length > 0 ? (
            allInsurance.map((insurance) => (
              <Card key={insurance.id} elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: 'rgba(245,158,11,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F59E0B', flexShrink: 0 }}>
                      <BusinessCenter sx={{ fontSize: 18 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: 15 }}>{insurance.name}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {new Date(insurance.createdAt).toLocaleDateString()}
                    </Typography>
                    <Box>
                      <IconButton size="small" color="primary" onClick={() => handleEditClick(insurance)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(insurance)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Card>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
              <BusinessCenter sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
              <Typography>No insurance companies found. Create one to get started.</Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allInsurance.length > 0 ? (
                  allInsurance.map((insurance) => (
                    <TableRow key={insurance.id}>
                      <TableCell>
                        <Typography variant="body1" fontWeight={500}>{insurance.name}</Typography>
                      </TableCell>
                      <TableCell>{new Date(insurance.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary" onClick={() => handleEditClick(insurance)}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(insurance)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography color="text.secondary" py={4}>
                        No insurance companies found. Create one to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Insurance Company</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the name of the insurance company.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus
            label="Company Name"
            fullWidth
            value={insuranceName}
            onChange={(e) => setInsuranceName(e.target.value)}
            error={insuranceName.length > 0 && insuranceName.length < 2}
            helperText={insuranceName.length > 0 && insuranceName.length < 2 ? 'Name must be at least 2 characters' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateConfirm}
            variant="contained"
            disabled={insuranceName.trim().length < 2 || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Insurance Company</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update the insurance company name.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus
            label="Company Name"
            fullWidth
            value={insuranceName}
            onChange={(e) => setInsuranceName(e.target.value)}
            error={insuranceName.length > 0 && insuranceName.length < 2}
            helperText={insuranceName.length > 0 && insuranceName.length < 2 ? 'Name must be at least 2 characters' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditConfirm}
            variant="contained"
            disabled={insuranceName.trim().length < 2 || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Insurance Company</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <DialogContentText>
            Are you sure you want to delete <strong>{selectedInsurance?.name}</strong>?
            {!error && ' This action cannot be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
